/**
 * Utility functions for downloading CSV files from an API.
 *
 * @see .documentation/Download_Endpoints.md for more details on the API endpoints used for downloading CSV files.
 */
import axios from "axios";

import apiClient from "#/redux/api/ApiClient";

export const FormatCSV = "text/csv";

/**
 * Requests a token for downloading a CSV file
 *
 * Request is based on the provided filter and then opens the download URL in a new tab.
 *
 * @param {ApiClient} api
 * @param {string} baseURL
 * @param {ApiFilter} filter
 * @returns {Promise<string>}
 */
export async function requestDownloadURL(api, baseURL, format, filter) {
  if (format !== FormatCSV) {
    // right now we (and the backend) only support CSV,
    // but we can easily extend this in the future if needed
    throw new Error(`Unsupported format: ${format}`);
  }

  if (!baseURL) {
    throw new Error("Base URL is required");
  }

  if (baseURL.endsWith("/") || baseURL.endsWith("/download")) {
    throw new Error("Base URL should not end with a slash or /download");
  }

  const payload = {
    filter: typeof filter === "string" ? filter : filter.toQueryString(false),
    pageSize: 100000,
  };

  return api
    .post(`${baseURL}/download`, payload)
    .then((response) => response.data.downloadUrl)
    .catch((error) => {
      console.error("Error during download:", error);
      throw error;
    });
}

/**
 * Downloads a CSV file
 *
 * First, it obtains a token using the requestToken function and then opens the download URL in a new tab.
 *
 * @param {ApiClient} api
 * @param {string} baseURL
 * @param {ApiFilter} filter
 * @param {string} zastopnistvoId
 * @param {string} token
 * @returns {Promise<void>}
 */
export default async function download({
  api,
  baseURL,
  format,
  filter,
  zastopnistvoId,
  token,
}) {
  const url = await requestDownloadURL(api, baseURL, format, filter);

  const response = await axios.get(url, {
    headers: {
      "x-zastopnistvo-id": zastopnistvoId,
      authorization: "Bearer " + token,
    },
  });

  const blob = new Blob([response.data], {
    type: "text/csv;charset=utf-8;",
  });

  const downloadUrl = window.URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = downloadUrl;
  a.download = "izvoz.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();

  window.URL.revokeObjectURL(downloadUrl);
}

export const downloadAsCSV = async ({
  baseURL,
  filter = undefined,
  zastopnistvoId,
  token,
}) =>
  await download({
    api: apiClient,
    baseURL,
    format: FormatCSV,
    filter,
    zastopnistvoId,
    token,
  });
